import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

// Always use relative paths so the app works on any domain (preview, production, custom)
const API_URL = '';

const AdminAuthContext = createContext(null);

/**
 * In-memory token store — NOT in localStorage (XSS-safe), NOT in cookies (proxy-safe).
 * Token survives React re-renders but clears on page refresh (acceptable for admin panel).
 */
let memoryToken = null;

/**
 * Pure XMLHttpRequest — no withCredentials, no cookies, no postMessage interference.
 * Token is sent via Authorization header from in-memory store.
 */
const apiRequest = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);

      // Set headers — Authorization + Content-Type
      const headers = options.headers || {};
      if (memoryToken && !headers['Authorization']) {
        headers['Authorization'] = `Bearer ${memoryToken}`;
      }
      Object.keys(headers).forEach(key => xhr.setRequestHeader(key, headers[key]));

      xhr.timeout = 20000;

      xhr.onload = () => {
        let data = null;
        try { data = JSON.parse(xhr.responseText); } catch (_) { /* non-JSON response */ }
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
      };

      xhr.onerror = () => reject(new Error('Impossible de contacter le serveur'));
      xhr.ontimeout = () => reject(new Error('Le serveur ne repond pas'));

      xhr.send(options.body || null);
    } catch (err) {
      // Catches synchronous throws from browser extensions intercepting XHR
      reject(new Error('Erreur de connexion — desactivez vos extensions navigateur et reessayez'));
    }
  });
};

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const setupResult = await apiRequest(`${API_URL}/api/auth/check-setup`);
      setSetupRequired(setupResult.data?.setup_required ?? true);

      // If we have a token in memory, verify it
      if (memoryToken) {
        const meResult = await apiRequest(`${API_URL}/api/auth/me`);
        if (meResult.ok && meResult.data) {
          setUser(meResult.data);
        } else {
          memoryToken = null;
        }
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setSetupRequired(true);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const result = await apiRequest(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!result.data) throw new Error('Reponse invalide du serveur');
    if (!result.ok) throw new Error(result.data.detail || 'Identifiants incorrects');

    // Store token in memory (not localStorage, not cookies)
    memoryToken = result.data.access_token;
    setUser(result.data.user);
    return result.data.user;
  };

  const setup = async (email, password, name) => {
    const result = await apiRequest(`${API_URL}/api/auth/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name })
    });

    if (!result.data) throw new Error('Reponse invalide du serveur');
    if (!result.ok) throw new Error(result.data.detail || 'Echec de la configuration');

    setSetupRequired(false);
    return login(email, password);
  };

  const logout = async () => {
    try {
      await apiRequest(`${API_URL}/api/auth/logout`, { method: 'POST' });
    } catch (err) { console.error('Logout cleanup failed:', err); }
    memoryToken = null;
    setUser(null);
  };

  return (
    <AdminAuthContext.Provider value={{
      user, loading, setupRequired,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      login, logout, setup,
      token: memoryToken
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return context;
};

/**
 * Authenticated API helper — sends token via Authorization header from memory.
 */
const authenticatedRequest = async (endpoint, options = {}) => {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  const result = await apiRequest(`${API_URL}${endpoint}`, { ...options, headers });

  if (result.status === 401) {
    memoryToken = null;
    window.location.href = '/ogd-access-gate';
    throw new Error('Session expiree');
  }

  return result;
};

export const adminApi = {
  async get(endpoint) {
    const r = await authenticatedRequest(endpoint);
    if (!r.ok) throw new Error('Erreur de requete');
    return r.data;
  },

  async post(endpoint, body) {
    const r = await authenticatedRequest(endpoint, { method: 'POST', body: JSON.stringify(body) });
    if (!r.ok) throw new Error(r.data?.detail || 'Erreur de requete');
    return r.data;
  },

  async put(endpoint, body) {
    const r = await authenticatedRequest(endpoint, { method: 'PUT', body: JSON.stringify(body) });
    if (!r.ok) throw new Error(r.data?.detail || 'Erreur de requete');
    return r.data;
  },

  async patch(endpoint, body) {
    const r = await authenticatedRequest(endpoint, { method: 'PATCH', body: JSON.stringify(body) });
    if (!r.ok) throw new Error(r.data?.detail || 'Erreur de requete');
    return r.data;
  },

  async delete(endpoint) {
    const r = await authenticatedRequest(endpoint, { method: 'DELETE' });
    if (!r.ok) throw new Error(r.data?.detail || 'Erreur de requete');
    return r.data;
  },

  async upload(endpoint, formData) {
    return new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}${endpoint}`, true);
        if (memoryToken) xhr.setRequestHeader('Authorization', `Bearer ${memoryToken}`);
        xhr.onload = () => {
          let data = null;
          try { data = JSON.parse(xhr.responseText); } catch (_) { /* non-JSON */ }
          if (xhr.status >= 200 && xhr.status < 300) resolve(data);
          else reject(new Error(data?.detail || 'Erreur upload'));
        };
        xhr.onerror = () => reject(new Error('Erreur de connexion'));
        xhr.send(formData);
      } catch (err) {
        reject(new Error('Erreur upload — desactivez vos extensions navigateur'));
      }
    });
  },

  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.upload('/api/admin/media/upload', formData);
  }
};

export default AdminAuthContext;
